import { useState, useEffect, useRef } from 'react';
import { AEDMap } from './aedmap';
import { AddAEDForm } from './aedform';
import { fetchAEDs } from './lib/api';
import { MapPin, Loader2, Plus } from 'lucide-react';
import SearchBar from './SearchBar';
import { idbGet, idbSet, OSM_CACHE_KEY } from './lib/osmdb';
import './aedmap.css';

function withTimeout(parentSignal, ms) {
  const controller = new AbortController();
  const onParentAbort = () => controller.abort();
  if (parentSignal.aborted) {
    controller.abort();
  } else {
    parentSignal.addEventListener('abort', onParentAbort);
  }
  const timer = setTimeout(() => controller.abort(), ms);
  const cleanup = () => {
    clearTimeout(timer);
    parentSignal.removeEventListener('abort', onParentAbort);
  };
  return { signal: controller.signal, cleanup };
}

class OverpassAggregateError extends Error {
  constructor(errors, message) {
    super(message);
    this.name = 'OverpassAggregateError';
    this.errors = errors;
  }
}

const OVERPASS_ENDPOINTS = [
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
  'https://overpass-api.de/api/interpreter'
];

// Query a single Overpass endpoint with a POST body
async function queryEndpoint(url, query, signal) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: `data=${encodeURIComponent(query)}`,
    signal
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  // Standardize nodes, ways, and relations into a point structure
  return (data.elements || [])
    .map(el => ({
      id: el.id,
      lat: el.lat || el.center?.lat,
      lon: el.lon || el.center?.lon,
      tags: el.tags
    }))
    .filter(el => el.lat && el.lon);
}

function App() {
  const [aeds, setAeds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [mapCenter, setMapCenter] = useState([48.8566, 2.3522]);
  const [userLocation, setUserLocation] = useState(null);
  const [osmNodes, setOsmNodes] = useState([]);
  const [isOsmLoading, setIsOsmLoading] = useState(true);
  const [osmError, setOsmError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [mapZoom, setMapZoom] = useState(13);

  // Active refs across re-renders
  const currentControllerRef = useRef(null);
  const lastFetchedCenterRef = useRef(null);
  const moveDebounceRef = useRef(null);
  const lastRequestTimeRef = useRef(0);
  const MIN_REQUEST_INTERVAL = 3000;

  const fetchOsmNearLocation = async (lat, lon) => {
    if (currentControllerRef.current) {
      currentControllerRef.current.abort();
    }
    const controller = new AbortController();
    currentControllerRef.current = controller;
    const signal = controller.signal;

    setIsOsmLoading(true);
    setOsmError(null);

    // ~30km bbox corrected for latitude/longitude scaling
    const latDelta = 30 / 111;
    const lonDelta = 30 / (111 * Math.cos((lat * Math.PI) / 180));
    const bbox = `${lat - latDelta},${lon - lonDelta},${lat + latDelta},${lon + lonDelta}`;
    const q = `[out:json][timeout:20];nwr["emergency"="defibrillator"](${bbox});out center;`;

    const queryWithStagger = (parentSignal) => {
      return new Promise((resolve, reject) => {
        const errors = [];
        let settled = false;

        const tryNext = (index) => {
          if (settled || index >= OVERPASS_ENDPOINTS.length) {
            if (!settled && errors.filter(Boolean).length === OVERPASS_ENDPOINTS.length) {
              settled = true;
              reject(new OverpassAggregateError(errors.filter(Boolean), 'All Overpass endpoints failed'));
            }
            return;
          }

          const { signal: endpointSignal, cleanup } = withTimeout(parentSignal, 8000);

          queryEndpoint(OVERPASS_ENDPOINTS[index], q, endpointSignal)
            .then(nodes => {
              cleanup();
              if (!settled) {
                settled = true;
                resolve(nodes);
              }
            })
            .catch(err => {
              cleanup();
              if (parentSignal.aborted) return;
              errors[index] = err;
              if (!settled && errors.filter(Boolean).length === OVERPASS_ENDPOINTS.length) {
                settled = true;
                reject(new OverpassAggregateError(errors.filter(Boolean), 'All Overpass endpoints failed'));
              }
            });

          if (index + 1 < OVERPASS_ENDPOINTS.length) {
            setTimeout(() => tryNext(index + 1), 1500);
          }
        };

        tryNext(0);
      });
    };

    try {
      const nodes = await queryWithStagger(signal);
      if (signal.aborted) return;

      setOsmNodes(prevNodes => {
        const nodeMap = new Map(prevNodes.map(n => [n.id, n]));
        nodes.forEach(n => nodeMap.set(n.id, n));
        const combined = Array.from(nodeMap.values());
        idbSet(OSM_CACHE_KEY, combined).catch(() => {});
        return combined;
      });
    } catch (err) {
      if (signal.aborted) return;
      console.warn('All Overpass endpoints failed:', err);
      setOsmError('Could not load nearby OpenStreetMap AEDs.');
    } finally {
      if (!signal.aborted) setIsOsmLoading(false);
    }
  };

  const loadAEDs = async () => {
    try {
      const data = await fetchAEDs();
      setAeds(data);
    } catch (error) {
      console.error('Error loading AEDs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAEDs();

    idbGet(OSM_CACHE_KEY)
      .then(cached => {
        if (cached) setOsmNodes(cached);
      })
      .catch(() => {});

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const location = [position.coords.latitude, position.coords.longitude];
          setUserLocation(location);
          setMapCenter(location);
          lastFetchedCenterRef.current = { lat: location[0], lng: location[1] };
          fetchOsmNearLocation(location[0], location[1]);
        },
        error => {
          console.warn('Geolocation denied or failed, using default center:', error);
          lastFetchedCenterRef.current = { lat: mapCenter[0], lng: mapCenter[1] };
          fetchOsmNearLocation(mapCenter[0], mapCenter[1]);
        },
        { timeout: 8000 }
      );
    } else {
      lastFetchedCenterRef.current = { lat: mapCenter[0], lng: mapCenter[1] };
      fetchOsmNearLocation(mapCenter[0], mapCenter[1]);
    }
  }, []);

  const handleAEDAdded = () => {
    loadAEDs();
  };

  const MOVE_THRESHOLD_DEG = 0.05;

  const handleMapMove = center => {
    if (lastFetchedCenterRef.current) {
      const latDiff = Math.abs(lastFetchedCenterRef.current.lat - center.lat);
      const lngDiff = Math.abs(lastFetchedCenterRef.current.lng - center.lng);
      if (latDiff < MOVE_THRESHOLD_DEG && lngDiff < MOVE_THRESHOLD_DEG) return;
    }

    if (moveDebounceRef.current) clearTimeout(moveDebounceRef.current);

    moveDebounceRef.current = setTimeout(() => {
      const now = Date.now();
      const wait = Math.max(0, MIN_REQUEST_INTERVAL - (now - lastRequestTimeRef.current));
      setTimeout(() => {
        lastFetchedCenterRef.current = center;
        lastRequestTimeRef.current = Date.now();
        fetchOsmNearLocation(center.lat, center.lng);
      }, wait);
    }, 400);
  };

  const handleSearch = async address => {
    try {
      setIsSearching(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      const data = await response.json();

      if (data && data[0]) {
        const { lat, lon } = data[0];
        const newCenter = [parseFloat(lat), parseFloat(lon)];
        setMapCenter(newCenter);
        setMapZoom(18);
        lastFetchedCenterRef.current = { lat: newCenter[0], lng: newCenter[1] };
        fetchOsmNearLocation(newCenter[0], newCenter[1]);
      } else {
        alert('Aucun résultat trouvé pour cette adresse');
      }
    } catch (error) {
      console.error("Erreur lors de la recherche d'adresse :", error);
      alert("Erreur lors de la recherche d'adresse. Veuillez réessayer.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    if (userLocation) {
      setMapCenter(userLocation);
      lastFetchedCenterRef.current = { lat: userLocation[0], lng: userLocation[1] };
      fetchOsmNearLocation(userLocation[0], userLocation[1]);
    }
  };

  return (
    <div className="aedapp">
      <header className="aedapp-header">
        <div className="aedapp-header-inner">
          <div className="aedapp-header-title">
            <div className="aedapp-header-icon"></div>
            <h1 className="aedapp-title">AED MAP</h1>
          </div>
          <SearchBar
            onSearch={handleSearch}
            onClear={handleClearSearch}
            isLoading={isSearching}
          />
        </div>
      </header>

      <button onClick={() => setShowForm(true)} className="aedapp-fab">
        <Plus size={20} />
        Add an AED
      </button>

      <main className="aedapp-main">
        <div className="aedapp-card">
          <div className="aedapp-card-bar">
            <div className="aedapp-count">
              <MapPin size={18} />
              <span>
                {aeds.length + osmNodes.length} defibrillator{(aeds.length + osmNodes.length) !== 1 ? 's' : ''} available
              </span>
            </div>
          </div>

          <div className="aedapp-mapwrap">
            {isOsmLoading && (
              <div
                className="aedapp-toast-loader"
                style={{
                  position: 'absolute',
                  top: 64,
                  right: 16,
                  zIndex: 1000,
                  background: 'white',
                  padding: '8px 14px',
                  borderRadius: '20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  fontWeight: 500
                }}
              >
                <Loader2 className="aedapp-spinner" size={16} />
                <span>Fetching nearby AEDs...</span>
              </div>
            )}
            {isLoading ? (
              <div className="aedapp-loading">
                <div className="aedapp-loading-inner">
                  <Loader2 className="aedapp-spinner" size={40} />
                  <p>Loading map...</p>
                </div>
              </div>
            ) : (
              <AEDMap
                aeds={aeds}
                center={mapCenter}
                zoom={mapZoom}
                osmNodes={osmNodes}
                onMapMove={handleMapMove}
              />
            )}
          </div>
        </div>
      </main>

      {showForm && (
        <AddAEDForm
          onAEDAdded={handleAEDAdded}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

export default App;